import type { JSONContent } from '@tiptap/core'
import type { Currency } from '@/types'
import type { Forms } from '@/services/forms/formTypes'
import type { PaletteColor } from '@/components/shared/color/types'
import type { DealsPipelineTemplateSystemStageId } from '@/services/domain/deals/types'
import type { TmCountryCode } from '@/services/types'
import type { SelectContactFieldConfig } from '@/services/forms/types'
import type { EmojiNative } from '@/services/forms/tmTiptap/types'

export const SUCCESS_DEFAULT_STAGE_NAME: DealsPipelineTemplateSystemStageId = 'won'
export const FAILED_DEFAULT_STAGE_NAME: DealsPipelineTemplateSystemStageId = 'lost'

export const moveAllDealsFormId = 'DealMoveAllFormService' satisfies Forms

export enum CreateOrUpdatePipelineFormFields {
  name = 'name',
  currency = 'currency',
  description = 'description',
}

export type CreateOrUpdatePipelineFormSchema = {
  [CreateOrUpdatePipelineFormFields.name]: {
    text: string
    emoji: { native: EmojiNative }
  }
  [CreateOrUpdatePipelineFormFields.currency]: Currency
  [CreateOrUpdatePipelineFormFields.description]?: string
}

export enum DealMoveAllFormFieldNames {
  stage = 'stage',
}

export type DealMoveAllFormScheme = {
  [DealMoveAllFormFieldNames.stage]: string
}

export type DealMoveAllParamsPartial = {
  //
}

export enum DealCustomStageFormFields {
  name = 'name',
  color = 'color',
  probability = 'probability',
}

export type DealCustomStageFormScheme = {
  [DealCustomStageFormFields.name]: string
  [DealCustomStageFormFields.color]: PaletteColor
  [DealCustomStageFormFields.probability]: number
}

export enum DealSystemStageFormFields {
  name = 'name',
}

export type DealSystemStageFormScheme = {
  [DealSystemStageFormFields.name]: string
}

export enum DealCardAddContactFormFields {
  contact = 'contact',
}

export type DealCardAddContactFormScheme = {
  [DealCardAddContactFormFields.contact]: SelectContactFieldConfig['value']
}

export enum DealTransferOwnershipFormFields {
  owner = 'assigneeId',
}

export type DealTransferOwnershipParamsPartial = {
  //
}

export type DealTransferOwnershipFormScheme = {
  [DealTransferOwnershipFormFields.owner]: string
}

export enum CreateDealFormFields {
  contactPerson = 'contactPerson',
  title = 'title',
  description = 'description',
  pipeline = 'pipeline',
  stage = 'stage',
  estimatedValue = 'estimatedValue',
  dealOwner = 'dealOwner',
  estimatedCloseDate = 'estimatedCloseDate',
  probability = 'probability',
}

export type CreateDealFormScheme = {
  [CreateDealFormFields.contactPerson]: SelectContactFieldConfig['value']
  [CreateDealFormFields.title]: string
  [CreateDealFormFields.description]: JSONContent
  [CreateDealFormFields.pipeline]: string
  [CreateDealFormFields.stage]: string
  [CreateDealFormFields.estimatedValue]: number
  [CreateDealFormFields.dealOwner]: string
  [CreateDealFormFields.estimatedCloseDate]: string
  [CreateDealFormFields.probability]: number
}

export enum PipelineDefaultOwnerFormFields {
  dealOwner = 'dealOwner',
}

export type PipelineDefaultOwnerFormScheme = {
  [PipelineDefaultOwnerFormFields.dealOwner]: string
}

export enum DealCustomStageDeleteApproach {
  delete = 'delete',
  moveAll = 'moveALl',
}

export enum DealCustomStageDeleteFieldNames {
  approach = 'approach',
  stage = 'stage',
}

export type DealCustomStageDeleteFormScheme = {
  [DealCustomStageDeleteFieldNames.approach]: DealCustomStageDeleteApproach
  [DealCustomStageDeleteFieldNames.stage]: string
}

export enum DealViewFormFieldNames {
  title = 'title',
  description = 'description',
}

export type DealViewFormScheme = {
  [DealViewFormFieldNames.title]: string
  [DealViewFormFieldNames.description]?: JSONContent
}

export type DealTimelineCommentFormSchema = {
  dealId: string
  text: JSONContent
}

export enum DealDetailsMainInfoFormFieldNames {
  owner = 'owner',
  stage = 'stage',
  estimatedValue = 'estimatedValue',
  estimatedCloseDate = 'estimatedCloseDate',
  probability = 'probability',
}

export type DealDetailsMainInfoFormSchema = {
  [DealDetailsMainInfoFormFieldNames.owner]: string
  [DealDetailsMainInfoFormFieldNames.stage]: string
  [DealDetailsMainInfoFormFieldNames.estimatedValue]: number
  [DealDetailsMainInfoFormFieldNames.estimatedCloseDate]: string
  [DealDetailsMainInfoFormFieldNames.probability]: number
}

export enum DealDetailsContactInfoFormFieldNames {
  contactPerson = 'contactPerson',
  contactEmail = 'contactEmail',
  contactPhone = 'contactPhone',
  contactCompany = 'contactCompany',
}

export type DealDetailsContactInfoFormSchema = {
  [DealDetailsContactInfoFormFieldNames.contactPerson]: SelectContactFieldConfig['value']
  [DealDetailsContactInfoFormFieldNames.contactEmail]: string
  [DealDetailsContactInfoFormFieldNames.contactPhone]: {
    phone: string
    countryCode: TmCountryCode
  }
  [DealDetailsContactInfoFormFieldNames.contactCompany]: string
}

export enum DealActivityItemFormFieldNames {
  name = 'name',
  checked = 'checked',
}

export type DealActivityItemFormScheme = {
  [DealActivityItemFormFieldNames.name]: string
  [DealActivityItemFormFieldNames.checked]: boolean
}
